/**
 * @file React hook — useBackgroundPrefill (state and side effects).
 * Background Data Prefill Hook
 * Handles prefilling form data from submission
 */

import axiosClient from "../../../../../../api/axiosClient";
import { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import { getSubmissionById } from "../../../../../../services/submissionService";
import { getBackgroundStageDisplayFields, getBackgroundDataTypeField } from "../../../../../../components/submission/backgroundStageFieldOrder";
import {
  dataTypeFieldOptions,
  normalizeDataTypeValue,
} from "../../../../../../components/submission/backgroundDataType";
import { resolveTimelineAvailableDefault } from "../utils/timelineDefaults";
import { resolveA1A3CheckboxDefault } from "../utils/dataCompletenessDefaults";
import {
  dataReviewerEmailKey,
  dataReviewerNameKey,
  normalizeDataReviewValue,
} from "../utils/backgroundDataReviewers";
import {
  BACKGROUND_GEO_SELECTION_FIELD,
  BACKGROUND_GEO_SPEC_FIELD,
  parseBackgroundGeoFromApi,
} from "../../../../../../utils/backgroundGeographicalSpecificity";

/** Type definition — Prefill Params. */
export interface PrefillParams {
  submissionId: string;
  formData: FormDataResponse;
  setValues: React.Dispatch<React.SetStateAction<Record<string, string | File>>>;
  setEnabledPairs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setRawMaterialGroups: React.Dispatch<React.SetStateAction<number[]>>;
  materials: { id: string; name: string; code: string; subMaterials: { id: string; name: string }[] }[];
  setSubMaterials: React.Dispatch<React.SetStateAction<{ id: string; name: string }[]>>;
  setRawMaterials: React.Dispatch<React.SetStateAction<{ id: string; name: string }[]>>;
}

/** Hook: use Background Prefill. */
export function useBackgroundPrefill() {
  const prefillFromSubmission = async ({
    submissionId,
    formData,
    setValues,
    setEnabledPairs,
    setRawMaterialGroups,
    materials,
    setSubMaterials,
    setRawMaterials,
  }: PrefillParams): Promise<void> => {
    try {
      const subRes = await getSubmissionById(submissionId);

      if (!subRes?.data?.success || !subRes.data.data) {
        console.warn("⚠️ No saved submission found for prefill.");
        return;
      }

      const submission = subRes.data.data;
      const bg = submission.background;
      const bgRawMats = subRes.data.data.backgroundRawMats || [];
      const bgReviewers =
        subRes.data.data.backgroundDataReviewers ||
        submission.backgroundDataReviewers ||
        [];

      const allFields = getBackgroundStageDisplayFields(formData);
      const timelineDefault = resolveTimelineAvailableDefault(allFields);
      const dataReviewField = allFields.find((f) => f.name === "dataReview");
      const dataReviewOptions =
        dataReviewField?.dropdown?.options ??
        dataReviewField?.dropdownValues ??
        [];
      const dataTypeOptions = dataTypeFieldOptions(
        getBackgroundDataTypeField(formData)
      );

      if (!bg) {
        console.log("ℹ️ No background section saved yet.");
        setValues((prev) => ({
          ...prev,
          timelineAvailable:
            String(prev.timelineAvailable ?? "").trim() || timelineDefault,
          a1_a3: String(prev.a1_a3 ?? "").trim() || resolveA1A3CheckboxDefault(),
        }));
        return;
      }

      const materialIdStr = bg.materialId?.toString() || "";

      // Load sub materials and raw materials if materialId exists
      if (materialIdStr) {
        // Find the material and set its sub materials
        const selectedMaterial = materials.find((m) => m.id === materialIdStr);
        if (selectedMaterial) {
          setSubMaterials(selectedMaterial.subMaterials || []);
        }

        // Fetch raw materials for this material
        try {
          const rawMatsRes = await axiosClient.get(
            `/raw-materials/material/${materialIdStr}`
          );
          if (rawMatsRes.status === 200 && rawMatsRes.data) {
            const normalizedRawMats = rawMatsRes.data.map((rm: any) => ({
              id: rm.id?.toString() || "",
              name: rm.name || "",
            }));
            setRawMaterials(normalizedRawMats);
          }
        } catch (error) {
          console.error("❌ Failed to fetch raw materials during prefill:", error);
        }
      }

      // Map backend field names -> UI field names
      const { geoSpec, geoSelection } = parseBackgroundGeoFromApi(bg);

      setValues((prev) => ({
        ...prev,
        // scalar fields
        materialId: materialIdStr,
        materialCode: bg.materialCode || "",
        subMaterialId: bg.subMaterialId?.toString() || "",
        subMaterialUnit: bg.subMaterialUnit?.toString() || "",
        finishedQuantity: bg.finishedQuantity || "",
        materialPropertiesAvailable: bg.materialPropertiesAvailable || "",
        density: bg.density || "",
        density_unit: bg.densityUnitId ? String(bg.densityUnitId) : "",
        uValue: bg.uValue || "",
        uValue_unit: bg.uValueUnitId ? String(bg.uValueUnitId) : "",
        compressiveStrength: bg.compressiveStrength || "",
        compressiveStrength_unit: bg.compressiveStrengthUnit
          ? String(bg.compressiveStrengthUnit)
          : "",
        tensileStrength: bg.tensileStrength || "",
        tensileStrength_unit: bg.tensileStrengthUnit
          ? String(bg.tensileStrengthUnit)
          : "",
        temperature: bg.temperature || "",
        temperature_unit: bg.temperatureUnitId
          ? String(bg.temperatureUnitId)
          : "",
        diameter: bg.diameter || "",
        diameterUnit: bg.diameterUnitId ? String(bg.diameterUnitId) : "",
        coverage: bg.coverage || "",
        coverageUnit: bg.coverageUnitId ? String(bg.coverageUnitId) : "",
        thickness: bg.thickness || "",
        thicknessUnit: bg.thicknessUnitId ? String(bg.thicknessUnitId) : "",
        recycledContent: bg.recycledContent || "",
        materialImageUrl: bg.materialImageUrl || "",
        publicationFileUrl: bg.publicationFileUrl || "",
        materialImage: bg.materialImageUrl || "",
        publicationFile: bg.publicationFileUrl || "",
        dataType:
          normalizeDataTypeValue(bg.dataType, dataTypeOptions) ||
          bg.dataType ||
          "",
        dataReview:
          normalizeDataReviewValue(bg.dataReview, dataReviewOptions) ||
          bg.dataReview ||
          "",
        dataAcquisition: bg.dataAcquisition || "",
        dataCompleteness: bg.dataCompleteness || "",
        [BACKGROUND_GEO_SPEC_FIELD]: geoSpec,
        [BACKGROUND_GEO_SELECTION_FIELD]: geoSelection,
        a1: bg.a1 ? "true" : "false",
        a2: bg.a2 ? "true" : "false",
        a3: bg.a3 ? "true" : "false",
        a1_a3: bg.a1_a3 ? "true" : "false",
        timelineAvailable:
          String(bg.timelineAvailable ?? "").trim() || timelineDefault,
        collectionStartYear: bg.collectionStartYear || "",
        collectionStartMonth: bg.collectionStartMonth || "",
        collectionStartDay: bg.collectionStartDay || "",
        collectionEndYear: bg.collectionEndYear || "",
        collectionEndMonth: bg.collectionEndMonth || "",
        collectionEndDay: bg.collectionEndDay || "",
        technologyId: bg.technologyId?.toString() || "",
        manufacturerId: bg.manufacturerId?.toString() || "",
      }));

      // Enable pair toggles if corresponding values exist
      setEnabledPairs((prev) => ({
        ...prev,
        density: !!bg.density,
        uValue: !!bg.uValue,
        compressiveStrength: !!bg.compressiveStrength,
        tensileStrength: !!bg.tensileStrength,
        temperature: !!bg.temperature,
        diameter: !!bg.diameter,
        coverage: !!bg.coverage,
        thickness: !!bg.thickness,
      }));

      // Prefill dynamic raw materials if present
      if (Array.isArray(bgRawMats) && bgRawMats.length > 0) {
        const groups = bgRawMats.map((_, idx) => idx + 1);
        setRawMaterialGroups(groups);

        setValues((prev) => {
          const next = { ...prev };
          bgRawMats.forEach((rm: any, idx: number) => {
            const i = idx + 1;
            const key = `rawMaterial_${String(i).padStart(2, "0")}`;
            const qtyKey = `${key}_qty`;
            const unitKey = `rawMaterialUnit_${String(i).padStart(2, "0")}`;
            next[key] = rm.rawMaterialName || "";
            next[qtyKey] = rm.qty?.toString() || "";
            next[unitKey] = rm.unitId?.toString() || "";
          });
          return next;
        });
      }

      if (Array.isArray(bgReviewers) && bgReviewers.length > 0) {
        setValues((prev) => {
          const next = { ...prev };
          bgReviewers.forEach((row: any, idx: number) => {
            const i = idx + 1;
            next[dataReviewerNameKey(i)] = row.name || "";
            next[dataReviewerEmailKey(i)] = row.email || "";
          });
          return next;
        });
      }

      console.log("✨ Background prefilled from submission data");
    } catch (err) {
      console.error("❌ Failed to prefill background submission:", err);
      throw err;
    }
  };

  return { prefillFromSubmission };
}

